import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { UnisphereProvider } from '@unisphere/runtime-react';
import { UnisphereWorkspaceType } from '@unisphere/runtime';
import {
  UsageReactRuntime,
  UsageReactRuntimeSettings,
  WidgetsRuntime,
  WidgetsRuntimeSettings
} from '@kaltura-corp/unisphere-plg-types';

interface UnisphereWrapperProps {
  children: ReactElement;
  ks: string;
  pid?: number;
}

export const UnisphereWrapper = ({ children, ks, pid }: UnisphereWrapperProps) => {
  const [workspace, setWorkspace] = useState<UnisphereWorkspaceType | null>(
    null
  );
  const [widgetsRuntime, setWidgetsRuntime] = useState<WidgetsRuntime | null>(null);
  const [usageReactRuntime, setUsageReactRuntime] = useState<UsageReactRuntime | null>(null);

  const env = useMemo(() => {
    const envMatch = window.location.hostname.match(/subscription\.(.+?)\.ovp\./);
    let env = '';
    const isLocalhost =
      window.location.hostname.startsWith('localhost') ||
      window.location.hostname.startsWith('http://localhost') ||
      window.location.hostname.startsWith('https://localhost');

    if (envMatch) {
      console.log('extracting env from hostname', envMatch[1]);
      env = envMatch[1];
    } else if (isLocalhost) {
      console.log(
        'no env match found in hostname and got isDev query param, assuming nvq2'
      );
      env = 'nvq2';
    } else {
      console.log('no env match found in hostname, assuming nvp1');
      env = 'nvp1';
    }
    return env;
  }, []);

  const onWorkspaceLoaded = (workspace: UnisphereWorkspaceType) => {
    setWorkspace(workspace);
    const widgetsRuntime = workspace.getRuntime<WidgetsRuntime>(
      'unisphere.widget.plg',
      'widgets'
    );
    setWidgetsRuntime(widgetsRuntime);
    const usageReactRuntime = workspace.getRuntime<UsageReactRuntime>(
      'unisphere.widget.plg',
      'usage-react'
    );
    setUsageReactRuntime(usageReactRuntime);
  };

  const widgetsRuntimeSettings: WidgetsRuntimeSettings = useMemo(() => {
    return {
      ks,
      productType: 'AVATAR_STUDIO',
    };
  }, [ks]);

  const usageReactRuntimeSettings = useMemo(() => {
    const settings: UsageReactRuntimeSettings = {
      ks,
      productType: 'AVATAR_STUDIO',
      showHeader: true,
    };
    if (pid) {
      settings.pid = String(pid);
    }
    return settings;
  }, [ks, pid]);

  useEffect(() => {
    widgetsRuntime?.updateSettings(widgetsRuntimeSettings);
    usageReactRuntime?.updateSettings(usageReactRuntimeSettings);
  }, [widgetsRuntime, usageReactRuntime, widgetsRuntimeSettings, usageReactRuntimeSettings]);

  useEffect(() => {
    workspace?.session.setData(prev => {
      return {
        ...prev,
        ks,
        partnerId: pid
      };
    });
  }, [ks, pid, workspace]);

  useEffect(() => {
    // clear KS on unmount to stop widgetsRuntime getUsageSummary polling (settings before session - to avoid re-syncing the old KS)
    return () => {
      widgetsRuntime?.updateSettings(prev => ({ ...prev, ks: '' }));
      workspace?.session.setData(prev => ({ ...prev, ks: '' }));
    };
  }, [widgetsRuntime, workspace]);

  if (!ks) {
    // If there's no KS, we shouldn't load the Unisphere workspace - it will fail to load the runtimes.
    // Show loader
    return <Loader/>;
  }

  return (
    <UnisphereProvider
      fallbackToExistingWorkspace={true}
      onWorkspaceLoaded={onWorkspaceLoaded}
      initialConfiguration={{
        appId: 'selfserve',
        appVersion: '1.0.0',
        session: {
          ks,
          partnerId: pid,
          analytics: {
            hostApplicationId: 15, // KPF
          }
        },
        serverUrl: `https://unisphere.${env}.ovp.kaltura.com/v1`,
        runtimes: [
          {
            widgetName: 'unisphere.widget.plg',
            runtimeName: 'widgets',
            settings: widgetsRuntimeSettings,
          },
          {
            widgetName: 'unisphere.widget.plg',
            runtimeName: 'usage-react',
            settings: usageReactRuntimeSettings
          },
        ],
        ui: {
          theme: 'light',
        },
      }}
    >
      {workspace ? children : <Loader/>}
    </UnisphereProvider>
  );
}

const Loader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
      }}
    >
      <CircularProgress size={48} />
    </Box>
  );
}
